import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { Sparkle, Warning, Buildings, Phone, EnvelopeSimple, Plus } from 'phosphor-react';
import { CollectionActivityWithUser } from '@dill/dill-shared';
import { useAppSelector, useAppDispatch } from '../../../../redux/store';
import {
  getSupplierCollectionTasks,
  updateCollectionActivity,
  deleteCollectionActivity,
} from '../../../../redux/services/collectionActivityService';
import { getSupplierBuyers } from '../../../../redux/services/supplierBuyersService';
import { ColFilterValue, DillSummaryCard } from '../../../general/Dill/DillTable';
import { DillSelect, DillModal, DillButton, DillDropdown } from '../../../general/Dill';
import SupplierCollectionTasksTable from './SupplierCollectionTasksTable';
import ViewTaskModal from './ViewTaskModal';
import EditTaskModal from '../CollectionActivityTimeline/modals/EditTaskModal';
import AddTaskModal from '../CollectionActivityTimeline/modals/AddTaskModal/AddTaskModal';
import ReviseAiRecommendationPromptModal from './ReviseAiRecommendationPromptModal';
import { APP_ENVIRONMENT } from '../../../../utils/constants';

function SupplierCollectionTasks() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { supplierTasks, loadingList } = useAppSelector((state) => state.collectionActivities);
  const { supplierBuyers, loadingList: buyersLoadingList } = useAppSelector(
    (state) => state.supplierBuyers
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const editingTaskId = searchParams.get('taskId');
  // Set by notification emails (e.g. tax exemption review) to open a task straight away.
  const viewTaskIdParam = searchParams.get('viewTaskId');

  const [activeTab, setActiveTab] = useState<'tasks' | 'recommendations'>('tasks');
  const [dueDateFilter, setDueDateFilter] = useState<
    'all' | 'overdue' | 'today' | 'upcoming' | 'noDueDate'
  >('all');
  const [creditManagerFilter, setCreditManagerFilter] = useState<string>('all');
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);
  const [updatingAction, setUpdatingAction] = useState<
    'start' | 'complete' | 'accept' | 'decline' | null
  >(null);
  const [viewingTask, setViewingTask] = useState<CollectionActivityWithUser | null>(null);

  // "+ New Task" flow: this page lists tasks across every customer, so there's no buyer already
  // in scope like there is on a single customer's page - pick one first, then open AddTaskModal.
  const [isCustomerPickerOpen, setIsCustomerPickerOpen] = useState(false);
  const [pickerBuyerId, setPickerBuyerId] = useState<string | null>(null);
  const [newTaskBuyerId, setNewTaskBuyerId] = useState<string | null>(null);
  const [isRevisePromptModalOpen, setIsRevisePromptModalOpen] = useState(false);

  const tabs = [
    { id: 'tasks' as const, label: 'Tasks' },
    { id: 'recommendations' as const, label: 'Dill Recommendations' },
  ];

  const isLoading = loadingList.includes('getSupplierCollectionTasks');

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(
        getSupplierCollectionTasks({
          supplierId: currentSupplier.id,
        })
      );
    }
  }, [currentSupplier?.id, dispatch]);

  const { regularTasks, dillRecommendedTasks } = useMemo(() => {
    const regular: CollectionActivityWithUser[] = [];
    const recommended: CollectionActivityWithUser[] = [];
    for (const task of supplierTasks) {
      if ((task.taskDetails as any)?.isDillRecommended) {
        recommended.push(task);
      } else {
        regular.push(task);
      }
    }
    return { regularTasks: regular, dillRecommendedTasks: recommended };
  }, [supplierTasks]);

  // Every credit manager who shows up on at least one task, for the filter dropdown.
  const creditManagerOptions = useMemo(() => {
    const byId = new Map<string, string>();
    let hasUnassigned = false;
    for (const task of regularTasks) {
      const manager = task.creditManagerUser;
      if (manager?.id) {
        byId.set(manager.id, `${manager.firstName} ${manager.lastName}`.trim());
      } else {
        hasUnassigned = true;
      }
    }
    const options = [
      { label: 'All Credit Managers', value: 'all' },
      ...Array.from(byId, ([value, label]) => ({ label, value })).sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
    ];
    if (hasUnassigned) options.push({ label: 'Unassigned', value: 'UNASSIGNED' });
    return options;
  }, [regularTasks]);

  // Scope everything downstream (due date counts, summary tiles, the table itself) to
  // the selected credit manager before applying the due-date pill filter on top.
  const creditManagerScopedTasks = useMemo(() => {
    if (creditManagerFilter === 'all') return regularTasks;
    if (creditManagerFilter === 'UNASSIGNED') {
      return regularTasks.filter((task) => !task.creditManagerUser?.id);
    }
    return regularTasks.filter((task) => task.creditManagerUser?.id === creditManagerFilter);
  }, [regularTasks, creditManagerFilter]);

  const dueDateCounts = useMemo(() => {
    const now = moment().startOf('day');
    const startOfWeek = moment().startOf('isoWeek');
    let overdue = 0;
    let today = 0;
    let upcoming = 0;
    let noDueDate = 0;
    let completedThisWeek = 0;
    for (const task of creditManagerScopedTasks) {
      const dueDate = (task.taskDetails as any)?.dueDate;
      const isCompleted = (task.taskDetails as any)?.status === 'COMPLETED';
      const completedAt = (task.taskDetails as any)?.completedAt;
      if (isCompleted && completedAt && moment(completedAt).isSameOrAfter(startOfWeek)) {
        completedThisWeek++;
      }
      if (!dueDate) {
        noDueDate++;
        continue;
      }
      const due = moment(dueDate);
      if (due.isBefore(now) && !isCompleted) {
        overdue++;
      } else if (due.isSame(now, 'day')) {
        today++;
      } else if (due.isAfter(now, 'day')) {
        upcoming++;
      }
    }
    return { overdue, today, upcoming, noDueDate, completedThisWeek };
  }, [creditManagerScopedTasks]);

  const filteredRegularTasks = useMemo(() => {
    if (dueDateFilter === 'all') return creditManagerScopedTasks;
    const now = moment().startOf('day');
    return creditManagerScopedTasks.filter((task) => {
      const dueDate = (task.taskDetails as any)?.dueDate;
      switch (dueDateFilter) {
        case 'overdue':
          return (
            dueDate &&
            moment(dueDate).isBefore(now) &&
            (task.taskDetails as any)?.status !== 'COMPLETED'
          );
        case 'today':
          return dueDate && moment(dueDate).isSame(now, 'day');
        case 'upcoming':
          return dueDate && moment(dueDate).isAfter(now, 'day');
        case 'noDueDate':
          return !dueDate;
        default:
          return true;
      }
    });
  }, [creditManagerScopedTasks, dueDateFilter]);

  const defaultColFilters = useMemo<Record<string, ColFilterValue> | undefined>(() => {
    const userId = user?.id;
    if (!userId) return undefined;
    return {
      assignedTo: { type: 'values', selected: [userId] },
    };
  }, [user]);

  // Counts driving the summary cards, scoped to open (non-completed, non-cancelled) tasks
  // for the currently selected credit manager.
  const openTaskMetrics = useMemo(() => {
    const openTasks = creditManagerScopedTasks.filter((task) => {
      const status = (task.taskDetails as any)?.status;
      return status !== 'COMPLETED' && status !== 'CANCELLED';
    });
    const accountsToTouch = new Set(openTasks.map((task) => task.buyerId)).size;
    const callsToMake = openTasks.filter(
      (task) => (task.taskDetails as any)?.taskType === 'CALL'
    ).length;
    const emailsToSend = openTasks.filter(
      (task) => (task.taskDetails as any)?.taskType === 'EMAIL'
    ).length;
    const highPriority = openTasks.filter(
      (task) => (task.taskDetails as any)?.priority === 'HIGH'
    ).length;
    return { accountsToTouch, callsToMake, emailsToSend, highPriority };
  }, [creditManagerScopedTasks]);

  const summaryCards: DillSummaryCard[] = useMemo(
    () => [
      {
        label: 'Accounts to Touch',
        value: String(openTaskMetrics.accountsToTouch),
        icon: <Buildings size={14} />,
        variant: 'default',
      },
      {
        label: 'Collection Calls to Make',
        value: String(openTaskMetrics.callsToMake),
        icon: <Phone size={14} />,
        variant: openTaskMetrics.callsToMake > 0 ? 'warning' : 'default',
      },
      {
        label: 'Collection Emails to Send',
        value: String(openTaskMetrics.emailsToSend),
        icon: <EnvelopeSimple size={14} />,
        variant: openTaskMetrics.emailsToSend > 0 ? 'warning' : 'default',
      },
      {
        label: 'High Priority Tasks',
        value: String(openTaskMetrics.highPriority),
        icon: <Warning size={14} />,
        variant: openTaskMetrics.highPriority > 0 ? 'danger' : 'default',
      },
    ],
    [openTaskMetrics]
  );

  const currentUser = useMemo(
    () =>
      user?.id ? { id: user.id, name: `${user.firstName} ${user.lastName}`.trim() } : undefined,
    [user]
  );

  const allTasks = useMemo(
    () => [...regularTasks, ...dillRecommendedTasks],
    [regularTasks, dillRecommendedTasks]
  );

  const selectedTask = useMemo(
    () => allTasks.find((t) => t.id === editingTaskId) || null,
    [allTasks, editingTaskId]
  );

  const refreshTasks = () => {
    if (currentSupplier?.id) {
      dispatch(
        getSupplierCollectionTasks({
          supplierId: currentSupplier.id,
        })
      );
    }
  };

  // These four dispatch updateCollectionActivity/deleteCollectionActivity, whose extraReducers
  // (collectionActivityReducers.ts) already apply the change to `supplierTasks` locally - no
  // refetch here, since a getSupplierCollectionTasks refetch flips `isLoading` and blanks the
  // whole table for a moment on every click, which read as the page reloading.
  const handleStartTask = async (activity: CollectionActivityWithUser) => {
    if (!currentSupplier?.id) return;
    setUpdatingTaskId(activity.id);
    setUpdatingAction('start');
    await dispatch(
      updateCollectionActivity({
        supplierId: currentSupplier.id,
        activityId: activity.id,
        updateData: {
          taskDetails: {
            ...activity.taskDetails,
            status: 'IN_PROGRESS',
          },
        } as any,
      })
    );
    setUpdatingTaskId(null);
    setUpdatingAction(null);
  };

  const handleCompleteTask = async (activity: CollectionActivityWithUser) => {
    if (!currentSupplier?.id) return;
    if (!user?.id) return;
    setUpdatingTaskId(activity.id);
    setUpdatingAction('complete');
    await dispatch(
      updateCollectionActivity({
        supplierId: currentSupplier.id,
        activityId: activity.id,
        updateData: {
          taskDetails: {
            ...activity.taskDetails,
            status: 'COMPLETED',
            completedAt: new Date(),
            completedBy: user.id,
          },
        } as any,
      })
    );
    setUpdatingTaskId(null);
    setUpdatingAction(null);
  };

  const handleAcceptTask = async (activity: CollectionActivityWithUser) => {
    if (!currentSupplier?.id) return;
    setUpdatingTaskId(activity.id);
    setUpdatingAction('accept');
    await dispatch(
      updateCollectionActivity({
        supplierId: currentSupplier.id,
        activityId: activity.id,
        updateData: {
          taskDetails: {
            ...activity.taskDetails,
            isDillRecommended: false,
          },
        } as any,
      })
    );
    setUpdatingTaskId(null);
    setUpdatingAction(null);
  };

  const handleDeclineTask = async (activity: CollectionActivityWithUser) => {
    if (!currentSupplier?.id) return;
    setUpdatingTaskId(activity.id);
    setUpdatingAction('decline');
    await dispatch(
      deleteCollectionActivity({
        supplierId: currentSupplier.id,
        activityId: activity.id,
      })
    );
    setUpdatingTaskId(null);
    setUpdatingAction(null);
  };

  const handleEditTask = (activity: CollectionActivityWithUser) => {
    setSearchParams({ taskId: activity.id });
  };

  const handleCloseEditModal = () => {
    setSearchParams({});
  };

  const handleViewTask = (activity: CollectionActivityWithUser) => {
    setViewingTask(activity);
  };

  const handleCloseViewModal = () => {
    setViewingTask(null);
    if (viewTaskIdParam) setSearchParams({});
  };

  useEffect(() => {
    if (!viewTaskIdParam || viewingTask) return;
    const task = supplierTasks.find((item) => item.id === viewTaskIdParam);
    if (task) setViewingTask(task);
  }, [viewTaskIdParam, supplierTasks]);

  const buyerOptions = useMemo(
    () =>
      supplierBuyers.map((buyer) => ({ label: buyer.name || 'Unnamed customer', value: buyer.id })),
    [supplierBuyers]
  );

  const handleOpenCustomerPicker = () => {
    setPickerBuyerId(null);
    setIsCustomerPickerOpen(true);
    if (currentSupplier?.id && supplierBuyers.length === 0) {
      dispatch(getSupplierBuyers({ supplierId: currentSupplier.id }));
    }
  };

  const handleCloseCustomerPicker = () => {
    setIsCustomerPickerOpen(false);
    setPickerBuyerId(null);
  };

  const handleConfirmCustomerPicker = () => {
    if (!pickerBuyerId) return;
    setNewTaskBuyerId(pickerBuyerId);
    handleCloseCustomerPicker();
  };

  const handleCloseAddTaskModal = () => {
    setNewTaskBuyerId(null);
  };

  const newTaskBuyer = useMemo(
    () => supplierBuyers.find((buyer) => buyer.id === newTaskBuyerId) || null,
    [supplierBuyers, newTaskBuyerId]
  );

  return (
    <div className="w-full h-full flex flex-col">
      {/* Tab Navigation */}
      <div className="sticky top-0 bg-white z-30 border-b border-gray-200">
        <div className="px-4 flex items-center justify-between">
          <div>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors cursor-pointer bg-transparent ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}>
                {tab.id === 'recommendations' && (
                  <Sparkle size={14} weight="fill" className="text-indigo-400 inline mr-1.5" />
                )}
                {tab.label}
              </button>
            ))}
          </div>
          <DillButton
            label="New Task"
            icon={<Plus size={14} />}
            onClick={handleOpenCustomerPicker}
            height={32}
          />
        </div>
      </div>

      {/* Tab Content */}
      <div className={`flex-1 p-4 ${activeTab === 'tasks' ? '' : 'hidden'}`}>
        {/* Toolbar: Due date pill filter tabs + Credit Manager filter */}
        <div className="flex items-center justify-between mb-3 bg-white border border-gray-100 rounded-xl shadow-sm px-3 py-2">
          <div className="inline-flex gap-0.5 p-0.5 bg-gray-100 rounded-lg">
            {[
              { key: 'all' as const, label: 'All' },
              { key: 'overdue' as const, label: 'Overdue' },
              { key: 'today' as const, label: 'Due Today' },
              { key: 'upcoming' as const, label: 'Upcoming' },
              { key: 'noDueDate' as const, label: 'No Due Date' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setDueDateFilter(tab.key)}
                className={
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-all ' +
                  (dueDateFilter === tab.key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700')
                }>
                {tab.label}
                {tab.key !== 'all' && (
                  <span
                    className={
                      'ml-1.5 tabular-nums ' +
                      (dueDateFilter === tab.key ? 'text-gray-400' : 'text-gray-300')
                    }>
                    {tab.key === 'overdue'
                      ? dueDateCounts.overdue
                      : tab.key === 'today'
                      ? dueDateCounts.today
                      : tab.key === 'upcoming'
                      ? dueDateCounts.upcoming
                      : dueDateCounts.noDueDate}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="w-56">
            <DillSelect
              value={creditManagerFilter}
              onChange={setCreditManagerFilter}
              options={creditManagerOptions}
              placeholder="All Credit Managers"
            />
          </div>
        </div>
        <SupplierCollectionTasksTable
          tasks={filteredRegularTasks}
          onStartTask={handleStartTask}
          onCompleteTask={handleCompleteTask}
          onAcceptTask={handleAcceptTask}
          onDeclineTask={handleDeclineTask}
          onEditTask={handleEditTask}
          onViewTask={handleViewTask}
          isLoading={isLoading}
          updatingTaskId={updatingTaskId}
          updatingAction={updatingAction}
          defaultColFilters={defaultColFilters}
          currentUser={currentUser}
          tableType="normal_tasks"
          summaryCards={summaryCards}
        />
      </div>
      <div className={`flex-1 p-4 ${activeTab === 'recommendations' ? '' : 'hidden'}`}>
        {dillRecommendedTasks.length > 0 ? (
          <SupplierCollectionTasksTable
            tasks={dillRecommendedTasks}
            onStartTask={handleStartTask}
            onCompleteTask={handleCompleteTask}
            onAcceptTask={handleAcceptTask}
            onDeclineTask={handleDeclineTask}
            onEditTask={handleEditTask}
            onViewTask={handleViewTask}
            isLoading={isLoading}
            updatingTaskId={updatingTaskId}
            updatingAction={updatingAction}
            height={'calc(100vh - 180px)'}
            // defaultColFilters={defaultColFilters}
            currentUser={currentUser}
            tableType="recommendation"
            toolbarRight={
              APP_ENVIRONMENT !== 'production' ? (
                <DillButton
                  label="Revise AI Prompt"
                  variant="ghost-blue"
                  icon={<Sparkle size={14} weight="bold" />}
                  onClick={() => setIsRevisePromptModalOpen(true)}
                  height={32}
                />
              ) : undefined
            }
          />
        ) : (
          <>
            {APP_ENVIRONMENT !== 'production' && (
              <div className="flex items-center justify-end mb-3">
                <DillButton
                  label="Revise AI Prompt"
                  variant="ghost-blue"
                  icon={<Sparkle size={14} weight="bold" />}
                  onClick={() => setIsRevisePromptModalOpen(true)}
                  height={32}
                />
              </div>
            )}
            <p className="text-xs text-gray-400 mt-2">
              No recommendations at this time. Check back later for suggested actions.
            </p>
          </>
        )}
      </div>

      <EditTaskModal
        open={!!editingTaskId && !!selectedTask}
        activity={selectedTask}
        onClose={handleCloseEditModal}
        onSuccess={() => {
          refreshTasks();
          handleCloseEditModal();
        }}
      />

      <ViewTaskModal
        open={!!viewingTask}
        activity={
          (viewingTask && supplierTasks.find((task) => task.id === viewingTask.id)) || viewingTask
        }
        onClose={handleCloseViewModal}
      />

      <DillModal
        open={isCustomerPickerOpen}
        onClose={handleCloseCustomerPicker}
        title="New Task"
        subtitle="Pick which customer this task is for."
        footer={
          <>
            <DillButton label="Cancel" variant="secondary" onClick={handleCloseCustomerPicker} />
            <DillButton
              label="Continue"
              onClick={handleConfirmCustomerPicker}
              isDisabled={!pickerBuyerId}
            />
          </>
        }>
        <DillDropdown
          label="Customer"
          items={buyerOptions}
          value={pickerBuyerId}
          containerClassName="w-full h-[34px]"
          onSelectChange={(item) => setPickerBuyerId(item?.value ?? null)}
          placeholder="Search customers…"
          isLoading={buyersLoadingList.includes('getSupplierBuyers')}
        />
      </DillModal>

      <AddTaskModal
        open={!!newTaskBuyerId}
        buyerId={newTaskBuyerId || ''}
        contacts={newTaskBuyer?.buyerContacts ?? undefined}
        onClose={handleCloseAddTaskModal}
        onSuccess={() => {
          refreshTasks();
          handleCloseAddTaskModal();
        }}
      />

      {currentSupplier && (
        <ReviseAiRecommendationPromptModal
          open={isRevisePromptModalOpen}
          supplierId={currentSupplier.id}
          initialFilterPrompt={
            currentSupplier.aiCollectionRecommendationsSettings?.filterPrompt || ''
          }
          initialPrioritizationPrompt={
            currentSupplier.aiCollectionRecommendationsSettings?.prioritizationPrompt || ''
          }
          initialUseCustomPromptOnly={
            !!currentSupplier.aiCollectionRecommendationsSettings?.useCustomPromptOnly
          }
          onClose={() => setIsRevisePromptModalOpen(false)}
          onSuccess={() => {
            refreshTasks();
            setIsRevisePromptModalOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default SupplierCollectionTasks;
